/** Create or rename a node type. */

import { useState } from 'react'
import { Modal } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Field } from '../components'
import { useNodeTypeMutations } from '../hooks'
import { CATEGORIES, type NodeCategory, type NodeTypeDef } from '../types/brain.types'

const CATEGORY_HELP: Record<NodeCategory, string> = {
  core: 'A thing the business is about — the records everything else hangs off.',
  secondary: 'Supporting detail: documents, interactions, risks.',
  derived: 'Computed from other records rather than loaded from a source.',
}

export function NodeTypeDialog({
  graphId,
  existing,
  onClose,
  onCreated,
}: {
  graphId: string
  existing?: NodeTypeDef | { node_type_id: string; key: string; category: NodeCategory; description: string | null }
  onClose: () => void
  onCreated?: (nodeTypeId: string) => void
}) {
  const { create, update } = useNodeTypeMutations(graphId)
  const toast = useToast()

  const [key, setKey] = useState(existing?.key ?? '')
  const [category, setCategory] = useState<NodeCategory>(existing?.category ?? 'core')
  const [description, setDescription] = useState(existing?.description ?? '')

  const busy = create.isPending || update.isPending

  async function save() {
    try {
      if (existing) {
        await update.mutateAsync({
          id: existing.node_type_id,
          body: { key: key.trim(), category, description: description.trim() || null },
        })
        toast.success(`Saved ${key.trim()}.`)
        onClose()
      } else {
        const created = await create.mutateAsync({
          key: key.trim(),
          category,
          description: description.trim() || null,
        })
        toast.success(`Created ${created.key}.`)
        onCreated?.(created.node_type_id)
      }
    } catch (error) {
      toast.error(error)
    }
  }

  return (
    <Modal
      title={existing ? `Edit ${existing.key}` : 'New node type'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={busy || !key.trim()}>
            {busy ? 'Saving…' : existing ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name" required help="Stored as PascalCase — 'account health' becomes AccountHealth.">
          <input
            className="input"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Account"
          />
        </Field>

        <Field label="Category" help={CATEGORY_HELP[category]}>
          <select
            className="select"
            value={category}
            onChange={(e) => setCategory(e.target.value as NodeCategory)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>

        <Field label="What is it?" help="Shown in the inspector, and given to the extractor as context.">
          <textarea
            className="textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="A company we sell to."
          />
        </Field>
      </div>
    </Modal>
  )
}
